import * as THREE from 'three';

export class Paddle {
  readonly mesh: THREE.Mesh;
  readonly halfWidth: number;
  readonly halfDepth: number;
  readonly height: number;
  /** 0..1 flash amount; ball contact spikes this to 1 and it decays each
   *  frame so the paddle briefly glows on hit. */
  private flashAmount = 0;
  private baseEmissiveIntensity = 0.45;
  /** Scratch color for the flash lerp — reused so we don't allocate each
   *  frame. Base emissive is stored on the material itself. */
  private readonly baseEmissive = new THREE.Color();
  private readonly flashEmissive = new THREE.Color(0xffffff);
  private readonly scratchEmissive = new THREE.Color();

  constructor(
    public width = 4.2,
    // Depth = the Z-axis extent (what reads as "height" in top-down view).
    // 0.7 = half a grid unit (GRID_UNIT is 1.4) so the paddle feels chunky.
    public depth = 0.7,
    height = 0.28,
  ) {
    this.halfWidth = width / 2;
    this.halfDepth = depth / 2;
    this.height = height;

    const geometry = new THREE.BoxGeometry(width, height, depth);
    const material = new THREE.MeshStandardMaterial({
      color: '#7cf0c2',
      roughness: 0.35,
      metalness: 0.25,
      emissive: '#0e3a2a',
      emissiveIntensity: 0.45,
    });
    this.mesh = new THREE.Mesh(geometry, material);
    this.mesh.castShadow = true;
    this.mesh.receiveShadow = true;
    // Z is set later via setZ() once the game knows the current FIELD.bottom.
    this.mesh.position.set(0, height / 2, 0);
    // Seed the flash-lerp base from the constructor emissive so hitFlash
    // works before setColor has been called.
    this.baseEmissive.copy(material.emissive);
  }

  /** Recolor the paddle (base + emissive). Called on each new game so the
   *  paddle re-rolls a color from the game palette. */
  setColor(color: string, emissive: string): void {
    const mat = this.mesh.material as THREE.MeshStandardMaterial;
    mat.color.set(color);
    mat.emissive.set(emissive);
    this.baseEmissive.set(emissive);
  }

  /** Trigger a subtle flash — Game calls this on ball contact. */
  hitFlash(): void {
    this.flashAmount = 1;
  }

  /** Decay the flash back to baseline; called each frame from Game. Lerps
   *  the emissive color toward white (row emissives are dark on their own)
   *  and gently spikes intensity so contact reads without stealing focus. */
  updateGlow(): void {
    const mat = this.mesh.material as THREE.MeshStandardMaterial;
    if (this.flashAmount > 0.001) {
      // Lerp only partway toward white so the paddle keeps its own color
      // identity through the flash instead of blowing out to pure white.
      this.scratchEmissive.copy(this.baseEmissive).lerp(this.flashEmissive, this.flashAmount * 0.55);
      mat.emissive.copy(this.scratchEmissive);
      mat.emissiveIntensity = this.baseEmissiveIntensity + this.flashAmount * 1.6;
      this.flashAmount *= 0.82;
    } else {
      mat.emissive.copy(this.baseEmissive);
      mat.emissiveIntensity = this.baseEmissiveIntensity;
    }
  }

  setX(x: number): void {
    this.mesh.position.x = x;
  }

  setZ(z: number): void {
    this.mesh.position.z = z;
  }

  get x(): number {
    return this.mesh.position.x;
  }

  get z(): number {
    return this.mesh.position.z;
  }

  dispose(): void {
    this.mesh.geometry.dispose();
    (this.mesh.material as THREE.Material).dispose();
  }
}
